import React, { useState, useEffect } from 'react';
import {
  Phone,
  Calendar,
  Shield,
  Clock,
  Award,
  Users,
  Heart,
  Activity,
  Stethoscope,
  Brain,
  Home as HomeIcon,
  Ambulance,
  UserCheck,
  Scissors,
  ClipboardList,
  CheckCircle,
  Star,
  MapPin,
  Mail,
  Globe,
  ArrowRight,
  HeartPulse,
  BedDouble,
  Menu,
  X
} from 'lucide-react';
import FloatingButtons from '../components/FloatingButtons';
import ContactButton from '../components/ContactButton';
import '../styles/Home.css';

const Home = () => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsHeaderVisible(currentScrollY < lastScrollY || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const services = [
    {
      icon: <BedDouble className="service-icon" />,
      title: "Complete ICU Setup at Home",
      description: "Fully equipped ICU facilities with ventilator, monitor,  Suction machine DVT pump , and all other medical equipment at your home."
    },
    {
      icon: <Stethoscope className="service-icon" />,
      title: "Critical & Non-Critical Nurses",
      description: "Trained and certified nurses for round-the-clock critical and non-critical patient care."
    },
    {
      icon: <Brain className="service-icon" />,
      title: "Neurology Care",
      description: "Specialized neurological care for stroke, paralysis, spine care ,Truma care, and brain injury patients."
    },
    {
      icon: <Activity className="service-icon" />,
      title: "Physiotherapy Services",
      description: "Neuro , Spine and ortho physiotherapy for rehabilitation and mobility improvement"
    },
    {
      icon: <Heart className="service-icon" />,
      title: "Palliative Care",
      description: "Compassionate end-of-life care focusing on comfort and quality of life."
    },
    {
      icon: <Users className="service-icon" />,
      title: "Elderly Care",
      description: "Dedicated care for senior citizens with personalized attention and support."
    },
    {
      icon: <HeartPulse className="service-icon" />,
      title: "Speech Therapy",
      description: "Professional speech and language therapy for communication disorders."
    },
    {
      icon: <Scissors className="service-icon" />,
      title: "Post-Operative Care",
      description: "Expert post-surgical care ensuring smooth recovery at home."
    },
    {
      icon: <ClipboardList className="service-icon" />,
      title: "Tube & Line Changes",
      description: "Tracheostomy tube , foley's catheter, Ryles tube, and  I V cannula management and changes."
    },
    {
      icon: <HomeIcon className="service-icon" />,
      title: "Stoma Care",
      description: "Professional stoma care and management by trained healthcare professionals."
    },
    {
      icon: <UserCheck className="service-icon" />,
      title: "Vital Check & Wound Care",
      description: "Regular  Home Visit for vital signs monitoring and expert wound care management."
    },
    {
      icon: <ClipboardList className="service-icon" />,
      title: "Dietician & Nutrition",
      description: "Personalized nutrition plans and dietary guidance for optimal health."
    },
    {
      icon: <Users className="service-icon" />,
      title: "GDA (Medical Trained Attendant)",
      description: "Medically trained attendants for daily patient care and assistance."
    },
    {
      icon: <Ambulance className="service-icon" />,
      title: "Ambulance, Air Ambulance & Cadaver van (Hearsa Van)",
      description: "24/7 emergency ambulance and air ambulance, Cadaver van (Hearsa Van) services for critical transfers."
    }
  ];

  const whyChooseUs = [
    {
      icon: <Clock className="feature-icon" />,
      title: "24/7 Availability",
      description: "Round-the-clock medical support whenever you need it."
    },
    {
      icon: <Award className="feature-icon" />,
      title: "Experienced Medical Team",
      description: "Certified healthcare professionals with years of experience."
    },
    {
      icon: <Heart className="feature-icon" />,
      title: "Personalized Care Plans",
      description: "Customized treatment plans tailored to individual needs."
    },
    {
      icon: <Shield className="feature-icon" />,
      title: "Emergency Response Ready",
      description: "Quick response team for medical emergencies."
    },
    {
      icon: <Stethoscope className="feature-icon" />,
      title: "Home ICU Specialists",
      description: "Expert team specializing in home-based intensive care."
    },
    {
      icon: <CheckCircle className="feature-icon" />,
      title: "Affordable & Transparent",
      description: "Competitive pricing with no hidden charges."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Contact Us",
      description: "Call or WhatsApp us to discuss your healthcare needs."
    },
    {
      number: "02",
      title: "Medical Assessment",
      description: "Our team evaluates the patient's condition and requirements."
    },
    {
      number: "03",
      title: "Customized Care Plan",
      description: "We create a personalized treatment plan for optimal care."
    },
    {
      number: "04",
      title: "Start Treatment",
      description: "Begin receiving professional healthcare services at home."
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      relation: "Son of Patient",
      rating: 5,
      text: "Excellent service! The nurses were professional and caring. My father received ICU-level care at home, which made his recovery much smoother."
    },
    {
      name: "Priya Sharma",
      relation: "Daughter of Patient",
      rating: 5,
      text: "Highly recommend Manav Heal Medicare. The physiotherapy sessions helped my mother regain mobility after her stroke. Very grateful for their support."
    },
    {
      name: "Amit Verma",
      relation: "Family Member",
      rating: 5,
      text: "Professional and compassionate team. They provided excellent elderly care for my grandmother. Available 24/7 and very responsive to our needs."
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="home-page">
      {/* Navigation Header */}
      <header className={`nav-header ${isHeaderVisible ? 'visible' : 'hidden'} ${isMenuOpen ? 'menu-open' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_8a7d01e3-a943-49c3-b304-e5f0a9c32b95/artifacts/jekliaow_Manav_Heal_Medicare_pvt-ltd_LOGO_1.png"
              alt="Manav Heal Medicare Logo"
            />
          </div>

          <button className="mobile-menu-toggle" onClick={toggleMenu} aria-label="Toggle menu">
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          <nav className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <button onClick={() => { scrollToSection('services'); setIsMenuOpen(false); }} className="nav-link">Services</button>
            <button onClick={() => { scrollToSection('about'); setIsMenuOpen(false); }} className="nav-link">About</button>
            <button onClick={() => { scrollToSection('contact'); setIsMenuOpen(false); }} className="nav-link">Contact</button>
            <div className="mobile-only-cta">
              <ContactButton
                type="call"
                label="Call Now"
                className="btn-primary"
              />
            </div>
          </nav>

          <div className="nav-cta">
            <ContactButton
              type="call"
              label="Call Now"
              className="btn-primary header-contact-btn"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              The  complete family Health care at your Home.
            </h1>
            <p className="hero-subtitle">
              24/7 Medical Support at Home – Compassionate, certified healthcare services delivered to your doorstep.
            </p>
            <div className="hero-cta">
              <ContactButton
                type="call"
                label="Call Now"
                className="btn-primary-large hero-contact-btn"
                icon={<Phone size={20} />}
              />
              <ContactButton
                type="whatsapp"
                label="WhatsApp"
                className="btn-secondary-large whatsapp-type hero-whatsapp-btn"
              />
            </div>
            <div className="trust-badges">
              <div className="badge">
                <Clock size={20} />
                <span>24/7 Available</span>
              </div>
              <div className="badge">
                <Award size={20} />
                <span>Certified Nurses</span>
              </div>
              <div className="badge">
                <Shield size={20} />
                <span>Emergency Support</span>
              </div>
              <div className="badge">
                <CheckCircle size={20} />
                <span>Quick Response</span>
              </div>
              <div className="badge">
                <Award size={20} />
                <span>Certified Physiotherapist</span>
              </div>
            </div>
          </div>
          <div className="hero-image-container">
            <div className="hero-image-wrapper">
              <img 
                src="/image.png" 
                alt="Complete Family Healthcare" 
                className="hero-main-image"
              />
              <div className="hero-image-overlay"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-image">
              <img
                src="/indian-healthcare.png"
                alt="Complete Family Healthcare"
              />
            </div>
            <div className="about-text">
              <h2 className="section-title">About Home Care</h2>
              <p className="about-description">
                Manav Heal Medicare Pvt. Ltd. provides professional home healthcare services that bring hospital-quality medical care to your doorstep. We specialize in both critical and non-critical care, offering complete ICU setup at home with state-of-the-art medical equipment.
              </p>
              <p className="about-description">
                Our team of trained medical professionals delivers personalized care plans tailored to each patient's unique needs. From elderly care to post-operative recovery, neurology support to physiotherapy, we ensure comprehensive healthcare services in the comfort and safety of your home.
              </p>
              <div className="about-highlights">
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Complete ICU Setup at Home</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Trained Medical Staff</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Personalized Care Plans</span>
                </div>

                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Neurology Care</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Elderly Care</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Palliative Care</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Stoma Care</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Tube & Line Change</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>Vital check & Wound Care</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle className="highlight-icon" />
                  <span>24/7 Medical Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Healthcare Services</h2>
            <p className="section-subtitle">
              Comprehensive medical services delivered to your home with professional care and compassion
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon-wrapper">
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Physiotherapy Highlight Section */}
      <section className="physiotherapy-section">
        <div className="container">
          <div className="physio-content">
            <div className="physio-text">
              <h2 className="section-title">Professional Physiotherapy Services at Home</h2>
              <p className="physio-intro">
                Our certified physiotherapists bring rehabilitation and recovery services directly to your home, helping you regain strength, mobility, and independence.
              </p>
              <ul className="physio-list">
                <li>
                  <CheckCircle className="list-icon" />
                  <span>Pre & Post Rehabilitation</span>
                </li>
                <li>
                  <CheckCircle className="list-icon" />
                  <span>Neuro Rehabilitation</span>
                </li>
                <li>
                  <CheckCircle className="list-icon" />
                  <span>Exercise Therapy</span>
                </li>
                <li>
                  <CheckCircle className="list-icon" />
                  <span>Sports Injury Rehabilitation</span>
                </li>
                <li>
                  <CheckCircle className="list-icon" />
                  <span>Pain Management & Mobility Support</span>
                </li>
              </ul>
              <ContactButton
                type="call"
                label="Book Physiotherapy Session"
                className="btn-primary-large physio-contact-btn"
                icon={<Calendar size={20} />}
              />
            </div>
            <div className="physio-images">
              <img
                src="/physiotherapy.png"
                alt="Physiotherapy Service"
                className="physio-img-main"
              />
              <img
                src="/indian-nurse.jpg"
                alt="Rehabilitation Therapy"
                className="physio-img-secondary"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Manav Heal Medicare?</h2>
            <p className="section-subtitle">
              Trusted by families for compassionate, professional home healthcare services
            </p>
          </div>
          <div className="features-grid">
            {whyChooseUs.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon-wrapper">
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Simple steps to get professional healthcare at your home
            </p>
          </div>
          <div className="steps-container">
            {steps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{step.number}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {index < steps.length - 1 && (
                  <ArrowRight className="step-arrow" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">
              Real experiences from families we've served
            </p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="star-icon" fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <p className="author-name">{testimonial.name}</p>
                  <p className="author-relation">{testimonial.relation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Need Medical Care at Home? We Are Just a Call Away.</h2>
            <p className="cta-subtitle">
              Our team is ready 24/7 to provide professional healthcare services at your home
            </p>
            <div className="cta-buttons">
              <ContactButton
                type="call"
                label="Call Now"
                className="btn-primary-large cta-contact-btn"
                icon={<Phone size={20} />}
              />
              <ContactButton
                type="whatsapp"
                label="WhatsApp Us"
                className="btn-secondary-large whatsapp-type cta-whatsapp-btn"
              />
              <ContactButton
                type="call"
                label="Book Consultation"
                className="btn-outline-large cta-consult-btn"
                icon={<Calendar size={20} />}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-subtitle">
              Contact us for any healthcare needs or inquiries
            </p>
          </div>
          <div className="contact-content">
            <div className="contact-info">
              <div className="info-card">
                <Phone className="info-icon" />
                <h3>Phone Numbers</h3>
                <p><a href="tel:9953530208">+91 9953530208</a></p>
                <p><a href="tel:8005592720">+91 8005592720</a></p>
              </div>
              <div className="info-card">
                <Mail className="info-icon" />
                <h3>Email</h3>
                <p><a href="mailto:manavheal@gmail.com">manavheal@gmail.com</a></p>
              </div>
              <div className="info-card">
                <Globe className="info-icon" />
                <h3>Website</h3>
                <p><a href="https://www.manavheal.com" target="_blank" rel="noopener noreferrer">www.manavheal.com</a></p>
              </div>
            </div>
            <div className="contact-form-container">
              <form className="contact-form" action="mailto:manavheal@gmail.com" method="post" encType="text/plain">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input type="text" id="name" name="name" placeholder="Enter your name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input type="tel" id="phone" name="phone" placeholder="Enter your phone number" required />
                </div>
                <div className="form-group">
                  <label htmlFor="service">Service Required</label>
                  <select id="service" name="service" required>
                    <option value="">Select a service</option>
                    <option value="ICU Setup">Complete ICU Setup at Home</option>
                    <option value="Nursing">Critical & Non-Critical Nursing</option>
                    <option value="Physiotherapy">Physiotherapy</option>
                    <option value="Elderly Care">Elderly Care</option>
                    <option value="Other">Other Services</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" rows="4" placeholder="Tell us about your healthcare needs" required></textarea>
                </div>
                <button type="submit" className="btn-primary-large">
                  <Mail size={20} />
                  <span>Send Message</span>
                </button>
              </form>
            </div>
          </div>

          {/* Google Maps */}
          <div className="map-container">
            <h3 className="map-title">Our Location</h3>
            <div className="map-wrapper">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224357.49842915355!2d77.04417!3d28.527554!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x52c2b7494e204dce!2sNew%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Manav Heal Medicare Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img
                src="https://customer-assets.emergentagent.com/job_8a7d01e3-a943-49c3-b304-e5f0a9c32b95/artifacts/jekliaow_Manav_Heal_Medicare_pvt-ltd_LOGO_1.png"
                alt="Manav Heal Medicare Logo"
                className="footer-logo"
              />
              <p className="footer-description">
                Professional home healthcare and nursing services available 24/7. Bringing hospital-quality care to your doorstep.
              </p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul className="footer-links">
                <li><button onClick={() => scrollToSection('about')}>About Us</button></li>
                <li><button onClick={() => scrollToSection('services')}>Services</button></li>
                <li><button onClick={() => scrollToSection('contact')}>Contact</button></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Services</h4>
              <ul className="footer-links">
                <li>ICU Setup at Home</li>
                <li>Nursing Care</li>
                <li>Physiotherapy</li>
                <li>Elderly Care</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact Info</h4>
              <ul className="footer-contact">
                <li>
                  <Phone size={16} />
                  <a href="tel:9953530208">+91 9953530208</a>
                </li>
                <li>
                  <Phone size={16} />
                  <a href="tel:8005592720">+91 8005592720</a>
                </li>
                <li>
                  <Mail size={16} />
                  <a href="mailto:manavheal@gmail.com">manavheal@gmail.com</a>
                </li>
                <li>
                  <Globe size={16} />
                  <a href="https://www.manavheal.com" target="_blank" rel="noopener noreferrer">www.manavheal.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Manav Heal Medicare Pvt. Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons */}
      <FloatingButtons />
    </div>
  );
};

export default Home;
